import { WebStorageStateStore, Log } from 'oidc-client-ts';
import { useState, useEffect, Suspense } from 'react';
import { ErrorBoundary, FallbackProps } from 'react-error-boundary';
import { HelmetProvider } from 'react-helmet-async';
import { AuthProvider } from 'react-oidc-context';
import { QueryClientProvider } from 'react-query';
import { ReactQueryDevtools } from 'react-query/devtools';
import { BrowserRouter as Router } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';

import 'react-toastify/dist/ReactToastify.css';
import { Button, Spinner } from '@/components/Elements';
import {
  NODE_ENV,
  COGNITO_ENDPOINT,
  COGNITO_CLIENT_ID,
  COGNITO_CUSTOM_DOMAIN,
  FRONEND_URI,
} from '@/config';
import { queryClient } from '@/lib/react-query';

Log.setLogger(console);

const ErrorFallback = ({ error }: FallbackProps) => {
  const [errMessage, setErrMessage] = useState('');
  useEffect(() => {
    let err = 'エラーが発生しました。画面を更新してください。';
    // バージョンアップエラー
    const chunkFailedMessage = /Loading chunk [\d]+ failed/;
    if (error?.message && chunkFailedMessage.test(error.message)) {
      err = 'バージョンが更新されました。再読込します。';
      window.location.reload();
    }
    // 認証エラー
    const authErrMessage = /COGNITO_AUTH_ERROR/;
    if (error?.message && authErrMessage.test(error.message)) {
      err = '認証情報切れました。再度ログインしてください。';
      window.location.assign(window.location.origin);
    }
    setErrMessage(err);
  }, [error]);
  return (
    <div
      className="text-red-500 w-screen h-screen flex flex-col justify-center items-center"
      role="alert"
    >
      <h2 className="text-lg font-semibold">{errMessage}</h2>
      <Button className="mt-4" onClick={() => window.location.assign(window.location.origin)}>
        更新
      </Button>
    </div>
  );
};

type AppProviderProps = {
  children: React.ReactNode;
};

export const signOutRedirect = () => {
  const clientId = COGNITO_CLIENT_ID;
  const logoutUri = new URL('/auth/logout', FRONEND_URI).toString();
  const cognitoDomain = COGNITO_CUSTOM_DOMAIN;
  window.location.href = `${cognitoDomain}/logout?client_id=${clientId}&logout_uri=${encodeURIComponent(
    logoutUri
  )}`;
};

export const COGNITO_AUTH_CONFIG = {
  authority: COGNITO_ENDPOINT,
  extraQueryParams: { lang: 'ja' },
  client_id: COGNITO_CLIENT_ID,
  redirect_uri: new URL('/auth/callback', FRONEND_URI).toString(),
  response_type: 'code',
  scope: 'openid profile email',
  userStore: new WebStorageStateStore({ store: window.localStorage }),
};

export const AppProvider = ({ children }: AppProviderProps) => {
  return (
    <>
      <ToastContainer autoClose={5000} hideProgressBar />
      <Suspense
        fallback={
          <div className="flex items-center justify-center w-screen h-screen">
            <Spinner size="xl" />
          </div>
        }
      >
        <ErrorBoundary FallbackComponent={ErrorFallback}>
          <HelmetProvider>
            <AuthProvider {...COGNITO_AUTH_CONFIG}>
              <QueryClientProvider client={queryClient}>
                {NODE_ENV !== 'test' && <ReactQueryDevtools />}

                <Router>{children}</Router>
              </QueryClientProvider>
            </AuthProvider>
          </HelmetProvider>
        </ErrorBoundary>
      </Suspense>
    </>
  );
};
